'use client';

import {
  Alert,
  AlertDescription,
  Avatar,
  AvatarFallback,
  Badge,
  Button,
  Center,
  Container,
  Heading,
  Inline,
  Spinner,
  Stack,
  Text,
} from '@cortex/ui';
import { Bot } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useFetchWithAuth } from '@/hooks/use-api';
import { getRandomPhrase } from '@/lib/loading-phrases';

import { ChatMessage } from './ChatMessage';
import { QuestionRenderer } from './QuestionRenderer';
import type {
  ConversationEntry,
  CurrentQuestion,
  QuestionDataOnly,
  QuestionTypeName,
} from './types';

interface InterviewContainerProps {
  requestId: string;
  onComplete?: () => void;
}

interface QuestionEventData {
  questionType: QuestionTypeName;
  data: QuestionDataOnly;
  questionNumber: number;
  messageId: string;
}

/**
 * Main interview container that manages question/answer flow
 *
 * Uses SSE for receiving questions, with a new request for each answer.
 * Maintains conversation history and displays as a chat interface.
 *
 * Flow:
 * 1. Connect to /stream?message=__start__ for first question
 * 2. User answers via UI
 * 3. Connect to /stream?message=<answer> for next question
 * 4. Repeat until complete
 */
export function InterviewContainer({
  requestId,
  onComplete,
}: InterviewContainerProps) {
  const fetchWithAuth = useFetchWithAuth();

  // Conversation state
  const [history, setHistory] = useState<ConversationEntry[]>([]);
  const [currentQuestion, setCurrentQuestion] =
    useState<CurrentQuestion | null>(null);
  const [questionNumber, setQuestionNumber] = useState(0);
  const [isThinking, setIsThinking] = useState(true);
  const [hasStarted, setHasStarted] = useState(false);

  // UI state
  const [error, setError] = useState<string | null>(null);
  const [loadingPhrase, setLoadingPhrase] = useState(getRandomPhrase);

  // Refs
  const abortControllerRef = useRef<AbortController | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const hasStartedRef = useRef(false);
  // Stable ref for onComplete to avoid dependency changes
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  // Auto-scroll to bottom when new content appears
  useEffect(() => {
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [history, currentQuestion, isThinking]);

  // Rotate loading phrases while in initial loading state (before first question)
  useEffect(() => {
    if (!isThinking || hasStarted) return;

    setLoadingPhrase(getRandomPhrase());
    const interval = setInterval(() => {
      setLoadingPhrase(getRandomPhrase());
    }, 3500);

    return () => clearInterval(interval);
  }, [isThinking, hasStarted]);

  // Fetch next question from the stream endpoint
  const fetchQuestion = useCallback(
    async (message: string) => {
      // Abort any in-flight request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      const controller = new AbortController();
      abortControllerRef.current = controller;

      setIsThinking(true);
      setError(null);

      try {
        const response = await fetchWithAuth(
          `/requests/${requestId}/interview/stream?message=${encodeURIComponent(message)}`,
          { signal: controller.signal }
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to fetch question');
        }

        // Read SSE stream
        const reader = response.body?.getReader();
        if (!reader) {
          throw new Error('No response body');
        }

        const decoder = new TextDecoder();

        while (true) {
          if (controller.signal.aborted) return;

          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value, { stream: true });
          const lines = chunk.split('\n');

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const event = JSON.parse(line.slice(6));

                if (controller.signal.aborted) return;

                switch (event.type) {
                  case 'thinking':
                    // Already showing thinking state
                    break;

                  case 'question': {
                    const questionData = event.data as QuestionEventData;
                    setCurrentQuestion({
                      questionType: questionData.questionType,
                      data: questionData.data,
                    });
                    setQuestionNumber(questionData.questionNumber);
                    setIsThinking(false);
                    setHasStarted(true);
                    break;
                  }

                  case 'complete':
                    setIsThinking(false);
                    setCurrentQuestion(null);
                    onCompleteRef.current?.();
                    break;

                  case 'error':
                    throw new Error(event.data?.message || 'Unknown error');

                  case 'chunk':
                  case 'message_complete':
                    // Fallback for text responses - shouldn't happen with tool-based system
                    break;
                }
              } catch {
                // Skip malformed SSE lines
              }
            }
          }
        }
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') return;
        if (controller.signal.aborted) return;

        const errorMessage =
          err instanceof Error ? err.message : 'Failed to load question';
        setError(errorMessage);
        setIsThinking(false);
      }
    },
    [requestId, fetchWithAuth]
  );

  // Handle answer submission
  const handleAnswer = useCallback(
    (answer: unknown) => {
      if (!currentQuestion) return;

      // Convert answer to string for the API and history
      let answerStr: string;
      if (typeof answer === 'string') {
        answerStr = answer;
      } else if (typeof answer === 'object' && answer !== null) {
        if ('selections' in answer) {
          answerStr = (answer as { selections: string[] }).selections.join(
            ', '
          );
        } else if ('confirmed' in answer) {
          answerStr = (answer as { confirmed: boolean }).confirmed
            ? 'Yes'
            : 'No';
        } else if ('action' in answer) {
          answerStr = (answer as { action: string }).action;
        } else {
          answerStr = JSON.stringify(answer);
        }
      } else {
        answerStr = String(answer);
      }

      // Add to conversation history
      setHistory((prev) => [
        ...prev,
        {
          id: `${questionNumber}-${Date.now()}`,
          question: currentQuestion,
          answer: answerStr,
          questionNumber,
        },
      ]);

      // Clear current question and fetch next
      setCurrentQuestion(null);
      void fetchQuestion(answerStr);
    },
    [currentQuestion, questionNumber, fetchQuestion]
  );

  // Start interview on mount
  // React 18 StrictMode double-renders, but refs persist across the unmount/remount cycle
  // We track if a fetch is in-flight to avoid duplicate requests
  // IMPORTANT: Do NOT abort the fetch in cleanup - it will kill the request during StrictMode remount
  const fetchInProgressRef = useRef(false);

  useEffect(() => {
    // If we've already started or a fetch is in progress, don't start again
    if (hasStartedRef.current || fetchInProgressRef.current) {
      return;
    }

    // Mark that we're starting a fetch
    fetchInProgressRef.current = true;
    hasStartedRef.current = true;

    void fetchQuestion('__start__');
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally run only on mount
  }, []);

  // Error state
  if (error) {
    return (
      <Alert variant="destructive" className="p-6">
        <Heading level={3} className="text-destructive mb-2 font-medium">
          Something went wrong
        </Heading>
        <AlertDescription>
          <Text color="error" size="sm">
            {error}
          </Text>
          <Button
            variant="link"
            onClick={() => void fetchQuestion('__start__')}
            className="mt-4"
          >
            Try again
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  // Initial loading state (before first question)
  if (!hasStarted && isThinking) {
    return (
      <Center className="animate-in fade-in py-16 duration-300">
        <Stack gap="md" align="center">
          <Stack gap="none" className="relative">
            <Spinner size="md" />
            <Stack
              gap="none"
              className="bg-primary absolute inset-0 h-10 w-10 animate-ping rounded-full opacity-20"
            />
          </Stack>
          <Text
            key={loadingPhrase}
            color="muted"
            align="center"
            className="animate-in fade-in slide-in-from-bottom-2 max-w-md duration-500"
          >
            {loadingPhrase}
          </Text>
        </Stack>
      </Center>
    );
  }

  // Chat interface (after first question received)
  return (
    <Container maxWidth="2xl" padding={false}>
      {/* Progress indicator */}
      {questionNumber > 0 && (
        <Inline justify="between" align="center" className="mb-4 text-base">
          <Text as="span" color="muted">
            Question {questionNumber}
            {history.length > 0 &&
              ` of ${history.length + (currentQuestion ? 1 : 0)}+`}
          </Text>
          <Badge variant="outline">Interview</Badge>
        </Inline>
      )}

      {/* Scrollable conversation area */}
      <Stack
        ref={scrollContainerRef}
        gap="lg"
        className="max-h-[60vh] overflow-y-auto pr-2"
      >
        {/* Conversation history */}
        {history.map((entry) => (
          <ChatMessage key={entry.id} entry={entry} />
        ))}

        {/* Current question */}
        {currentQuestion && (
          <Stack
            gap="none"
            className="animate-in fade-in slide-in-from-bottom-4 duration-300"
          >
            <QuestionRenderer
              questionType={currentQuestion.questionType}
              data={currentQuestion.data}
              onAnswer={handleAnswer}
            />
          </Stack>
        )}

        {/* Thinking indicator (inline, bouncing dots) */}
        {isThinking && hasStarted && (
          <Inline gap="sm" className="animate-in fade-in duration-200">
            <Avatar className="h-8 w-8 shrink-0">
              <AvatarFallback className="bg-primary/10">
                <Bot className="text-primary h-4 w-4" />
              </AvatarFallback>
            </Avatar>
            <Inline
              gap="xs"
              align="center"
              className="bg-muted/50 rounded-lg rounded-tl-none px-4 py-3"
            >
              <Text
                as="span"
                className="bg-muted-foreground h-2 w-2 animate-bounce rounded-full"
                style={{ animationDelay: '0ms' }}
              />
              <Text
                as="span"
                className="bg-muted-foreground h-2 w-2 animate-bounce rounded-full"
                style={{ animationDelay: '150ms' }}
              />
              <Text
                as="span"
                className="bg-muted-foreground h-2 w-2 animate-bounce rounded-full"
                style={{ animationDelay: '300ms' }}
              />
            </Inline>
          </Inline>
        )}

        {/* Scroll anchor */}
        <Stack ref={bottomRef} gap="none" />
      </Stack>
    </Container>
  );
}
